import React from 'react';
import { View } from 'react-native';
import { Switch } from '@/components/ui/switch';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { AvailabilityInterval } from '@/types/appointment';
import { timeToMinutes } from '@/utils/format-appointment';
import { TimeField } from './time-field';
import { useThemedHex } from '@/hooks/use-theme-colors';

interface Props {
  value: AvailabilityInterval[];
  onChange: (next: AvailabilityInterval[]) => void;
}

/** Ordem de exibição: segunda a domingo. weekday segue JS (0=dom … 6=sáb). */
const WEEKDAYS: { weekday: number; label: string; short: string }[] = [
  { weekday: 1, label: 'Segunda', short: 'Seg' },
  { weekday: 2, label: 'Terça', short: 'Ter' },
  { weekday: 3, label: 'Quarta', short: 'Qua' },
  { weekday: 4, label: 'Quinta', short: 'Qui' },
  { weekday: 5, label: 'Sexta', short: 'Sex' },
  { weekday: 6, label: 'Sábado', short: 'Sáb' },
  { weekday: 0, label: 'Domingo', short: 'Dom' },
];

const WEEK_DAYS_ONLY = [1, 2, 3, 4, 5];
const DEFAULT_INTERVAL = { startTime: '08:00', endTime: '18:00' };

/** Atalhos de grade: o prestador comum sai daqui em um toque. */
const PRESETS: {
  key: string;
  label: string;
  hint: string;
  weekdays: number[];
  intervals: { startTime: string; endTime: string }[];
}[] = [
  {
    key: 'comercial',
    label: 'Comercial',
    hint: 'Seg a sex, 8h às 18h',
    weekdays: WEEK_DAYS_ONLY,
    intervals: [{ startTime: '08:00', endTime: '18:00' }],
  },
  {
    key: 'almoco',
    label: 'Com pausa',
    hint: 'Seg a sex, 8h–12h e 13h–18h',
    weekdays: WEEK_DAYS_ONLY,
    intervals: [
      { startTime: '08:00', endTime: '12:00' },
      { startTime: '13:00', endTime: '18:00' },
    ],
  },
  {
    key: 'tarde',
    label: 'Só à tarde',
    hint: 'Seg a sáb, 13h às 19h',
    weekdays: [...WEEK_DAYS_ONLY, 6],
    intervals: [{ startTime: '13:00', endTime: '19:00' }],
  },
];

function intervalMinutes(interval: AvailabilityInterval): number {
  return Math.max(0, timeToMinutes(interval.endTime) - timeToMinutes(interval.startTime));
}

/** "8h", "7h30" — hora cheia sem zero à toa. */
function formatHours(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}min`;
  return rest ? `${hours}h${String(rest).padStart(2, '0')}` : `${hours}h`;
}

/**
 * Editor da grade de disponibilidade (A1): por dia da semana, um ou mais
 * intervalos de atendimento (permite pausa de almoço). Controlado.
 *
 * O que liga o dia é o switch — mesmo controle das Regras, para o toque ser
 * óbvio. Grade vazia começa pelos atalhos, não por um formulário em branco.
 */
export function GridEditor({ value, onChange }: Props) {
  const th = useThemedHex();
  const byDay = (weekday: number) =>
    value
      .filter((i) => i.weekday === weekday)
      .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

  const replaceDay = (weekday: number, intervals: AvailabilityInterval[]) => {
    const others = value.filter((i) => i.weekday !== weekday);
    onChange([...others, ...intervals]);
  };

  const toggleDay = (weekday: number, on: boolean) => {
    replaceDay(weekday, on ? [{ weekday, ...DEFAULT_INTERVAL }] : []);
  };

  const applyPreset = (preset: (typeof PRESETS)[number]) => {
    onChange(
      preset.weekdays.flatMap((weekday) =>
        preset.intervals.map((interval) => ({ weekday, ...interval })),
      ),
    );
  };

  const copyDayToAll = (weekday: number) => {
    const source = byDay(weekday);
    if (source.length === 0) return;
    onChange(
      WEEKDAYS.flatMap(({ weekday: target }) =>
        source.map((interval) => ({
          weekday: target,
          startTime: interval.startTime,
          endTime: interval.endTime,
        })),
      ),
    );
  };

  const addInterval = (weekday: number) => {
    const day = byDay(weekday);
    const last = day[day.length - 1];
    const start = last ? last.endTime : DEFAULT_INTERVAL.startTime;
    replaceDay(weekday, [...day, { weekday, startTime: start, endTime: '18:00' }]);
  };

  const updateInterval = (
    weekday: number,
    index: number,
    field: 'startTime' | 'endTime',
    time: string,
  ) => {
    const day = byDay(weekday);
    day[index] = { ...day[index], [field]: time };
    replaceDay(weekday, day);
  };

  const removeInterval = (weekday: number, index: number) => {
    const day = byDay(weekday).filter((_, i) => i !== index);
    replaceDay(weekday, day);
  };

  const activeDays = WEEKDAYS.filter(({ weekday }) => byDay(weekday).length > 0);
  const weeklyMinutes = value.reduce((total, i) => total + intervalMinutes(i), 0);
  const isEmpty = value.length === 0;

  return (
    <View className="gap-3">
      {isEmpty ? (
        <View
          className="gap-3 rounded-2xl border border-primary/25 bg-primary/5 p-4"
          style={{ borderCurve: 'continuous' }}
        >
          <View className="flex-row items-start gap-2.5">
            <Ionicons name="sparkles-outline" size={18} color="#FD5001" />
            <View className="flex-1">
              <ThemedText className="text-sm font-bold text-neutral-900">
                Comece por um atalho
              </ThemedText>
              <ThemedText className="text-xs leading-4 text-neutral-600">
                Escolha um horário pronto e ajuste depois. Sem nenhum dia ligado, seus
                clientes não conseguem marcar.
              </ThemedText>
            </View>
          </View>

          <View className="gap-2">
            {PRESETS.map((preset) => (
              <PressableScale
                key={preset.key}
                haptic
                onPress={() => applyPreset(preset)}
                className="flex-row items-center gap-3 rounded-2xl border border-neutral-200 bg-card px-4 py-3"
                style={{ borderCurve: 'continuous' }}
              >
                <View className="h-8 w-8 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
                  <Ionicons className="text-primary dark:text-white" name="time-outline" size={16} />
                </View>
                <View className="flex-1">
                  <ThemedText className="text-sm font-semibold text-neutral-900">
                    {preset.label}
                  </ThemedText>
                  <ThemedText className="text-xs text-neutral-500">{preset.hint}</ThemedText>
                </View>
                <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
              </PressableScale>
            ))}
          </View>
        </View>
      ) : (
        <View
          className="flex-row items-center gap-2 rounded-2xl border border-neutral-100 bg-card px-4 py-3"
          style={{ borderCurve: 'continuous' }}
        >
          <Ionicons className="text-emerald-700" name="calendar-outline" size={16} />
          <ThemedText className="flex-1 text-xs text-neutral-600">
            <ThemedText className="font-bold text-neutral-900">
              {activeDays.length} {activeDays.length === 1 ? 'dia' : 'dias'}
            </ThemedText>{' '}
            por semana · {formatHours(weeklyMinutes)} de atendimento
          </ThemedText>
          <PressableScale haptic onPress={() => onChange([])} hitSlop={8}>
            <ThemedText className="text-xs font-semibold text-neutral-400">Limpar</ThemedText>
          </PressableScale>
        </View>
      )}

      {WEEKDAYS.map(({ weekday, label }) => {
        const intervals = byDay(weekday);
        const active = intervals.length > 0;
        const dayMinutes = intervals.reduce((total, i) => total + intervalMinutes(i), 0);

        return (
          <View
            key={weekday}
            className="overflow-hidden rounded-2xl border bg-card"
            style={{
              borderCurve: 'continuous',
              borderColor: active ? '#FD500133' : th('#F5F5F5'),
            }}
          >
            <PressableScale
              haptic
              onPress={() => toggleDay(weekday, !active)}
              className="flex-row items-center gap-3 px-4 py-3.5"
              accessibilityRole="switch"
              accessibilityState={{ checked: active }}
              accessibilityLabel={`${label}: ${active ? 'atende' : 'folga'}`}
            >
              <View className="min-w-0 flex-1">
                <ThemedText className="text-base font-semibold text-neutral-900">
                  {label}
                </ThemedText>
                <ThemedText
                  className="text-xs"
                  style={{ color: active ? th('#047857') : th('#A3A3A3') }}
                >
                  {active
                    ? `${intervals.map((i) => `${i.startTime}–${i.endTime}`).join(', ')} · ${formatHours(dayMinutes)}`
                    : 'Folga — toque para atender neste dia'}
                </ThemedText>
              </View>

              <Switch checked={active} onCheckedChange={(on) => toggleDay(weekday, on)} />
            </PressableScale>

            {active && (
              <View className="gap-2 border-t border-neutral-100 px-4 pb-3.5 pt-3">
                {intervals.map((interval, index) => (
                  <View key={index} className="flex-row items-center gap-2">
                    <TimeField
                      value={interval.startTime}
                      label="Início"
                      onChange={(t) => updateInterval(weekday, index, 'startTime', t)}
                    />
                    <ThemedText className="text-neutral-400">até</ThemedText>
                    <TimeField
                      value={interval.endTime}
                      label="Fim"
                      onChange={(t) => updateInterval(weekday, index, 'endTime', t)}
                    />
                    <View className="flex-1" />
                    <PressableScale
                      haptic
                      onPress={() => removeInterval(weekday, index)}
                      hitSlop={8}
                      accessibilityLabel="Remover intervalo"
                    >
                      <Ionicons className="text-red-700" name="trash-outline" size={18} />
                    </PressableScale>
                  </View>
                ))}

                <View className="flex-row items-center justify-between pt-0.5">
                  <PressableScale
                    haptic
                    onPress={() => addInterval(weekday)}
                    className="flex-row items-center gap-1.5 py-1"
                  >
                    <Ionicons name="add-circle-outline" size={16} color="#FD5001" />
                    <ThemedText className="text-sm font-semibold text-primary">
                      Adicionar intervalo
                    </ThemedText>
                  </PressableScale>

                  <PressableScale
                    haptic
                    onPress={() => copyDayToAll(weekday)}
                    className="flex-row items-center gap-1.5 py-1"
                    accessibilityLabel={`Repetir ${label} nos outros dias`}
                  >
                    <Ionicons className="text-neutral-500" name="copy-outline" size={15} />
                    <ThemedText className="text-xs font-semibold text-neutral-500">
                      Repetir nos outros dias
                    </ThemedText>
                  </PressableScale>
                </View>
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
}
